import { AddContextualData } from '@opendoor/observability/slim';
import faviconPath from '@opendoor/shared-fe/faviconPath';
import { fetchLandingPageV2BySlug, getCashOfferModal } from 'cms';
import type {
  ILandingPageV2LayoutFields,
  ILandingPageV2LayoutProps,
  LandingPageV2Props,
} from 'cms';
import type { IIdToLoaderData } from 'cms/loaders';
import { DEPLOY_ENV } from 'components/globals';
import Layout from 'components/landing-pages-v2/Layout';
import { isTreatmentAnalyticsId } from 'components/landing-pages-v2/shared/header/glass/constants';
import { getContentPageImagePreloads } from 'components/shared/performance/preloads';
import type { IOpenGraphFieldsFields, ISeoBaseFieldsFields } from 'declarations/contentful';
import type { CacheSafeGetServerSideProps } from 'declarations/shared/serverSideProps';
import Head from 'next/head';
import { HOMEPAGE_SCHEMA } from 'schema';

const HERO_IMAGE_FIELDS: Record<string, string[]> = {
  lpComponentHeroV2: ['imageDesktopUrl', 'imageMobileUrl'],
  lpComponentHeroV2WithMarkers: ['imageDesktopUrl', 'imageMobileUrl'],
  lpComponentHeroCard: ['desktopCardImage', 'cardImage'],
  lpComponentHeroCta: ['doorwayImageUrl'],
};

const getHeroImageUrl = (body?: { content?: any[] }): string | undefined => {
  if (!body?.content) return undefined;
  for (const block of body.content) {
    if (block.nodeType !== 'embedded-entry-block') continue;
    const target = (block as any).data?.target;
    const contentTypeId = target?.sys?.contentType?.sys?.id;
    const imageFields = HERO_IMAGE_FIELDS[contentTypeId];
    if (!imageFields) continue;
    for (const field of imageFields) {
      const url = target?.fields?.[field];
      if (typeof url === 'string' && url) return url;
    }
  }
  return undefined;
};

const getSeoBaseTags = (fields?: ISeoBaseFieldsFields) => {
  const title = fields?.title;
  const metaDescription = fields?.metaDescription;
  const canonicalUrl = fields?.canonicalUrl;

  const robots: Array<string> = [];
  // only set these when they are explicitly set to false
  // otherwise assume indexing and follow should be allowed
  if (fields?.index === false) {
    robots.push('noindex');
  }
  if (fields?.follow === false) {
    robots.push('nofollow');
  }

  return (
    <>
      <title key="title">{title ?? 'Opendoor | Sell your home the minute you’re ready.'}</title>
      <link rel="shortcut icon" href={faviconPath({ railsEnv: DEPLOY_ENV })} type="image/png" />
      {metaDescription ? (
        <meta key="description" name="description" content={metaDescription} />
      ) : null}
      {canonicalUrl ? <link key="canonical" rel="canonical" href={canonicalUrl} /> : null}
      {robots.length > 0 ? <meta key="robots" name="robots" content={robots.join(',')} /> : null}
    </>
  );
};

const getOpenGraphTags = (openGraphFields: IOpenGraphFieldsFields) => {
  const { url, type, title, description, image, siteName } = openGraphFields;

  return (
    <>
      <meta key="og:title" property="og:title" content={title} />
      <meta key="og:type" property="og:type" content={type} />
      <meta key="og:url" property="og:url" content={url} />
      <meta key="og:image" property="og:image" content={image} />
      <meta key="og:description" property="og:description" content={description} />
      <meta key="og:site_name" property="og:site_name" content={siteName} />
      <meta key="twitter:url" name="twitter:url" content={url} />
      <meta key="twitter:title" name="twitter:title" content={title} />
      {description && (
        <meta key="twitter:description" name="twitter:description" content={description} />
      )}
      <meta key="twitter:image" name="twitter:image" content={image} />
    </>
  );
};

export const getJobPostingSchemaFromLoaderData = (idToLoaderData: IIdToLoaderData) => {
  for (const loaderData of Object.values(idToLoaderData)) {
    if (loaderData?.jobPostingSchema) {
      return loaderData.jobPostingSchema;
    }
  }
  return null;
};

// Loader-supplied per-page SEO overrides — used by entries (e.g. JD pages) whose
// dynamic({ ssr: false }) component cannot emit Head tags into the SSR HTML itself.
// The loader runs server-side, so its returned values are reliably reachable here.
export interface LoaderSeoOverride {
  title: string;
  canonicalUrl: string;
  metaDescription?: string;
  ogTitle?: string;
  ogUrl?: string;
  ogDescription?: string;
}

export const getSeoOverrideFromLoaderData = (
  idToLoaderData: IIdToLoaderData,
): LoaderSeoOverride | null => {
  for (const loaderData of Object.values(idToLoaderData)) {
    if (loaderData?.seoOverride) {
      return loaderData.seoOverride as LoaderSeoOverride;
    }
  }
  return null;
};

// True when a JD-style loader ran (entry has a `seoOverride` key) and explicitly
// returned a missing-job result — the Rippling fetch failed or the UUID was
// unknown. Lets the page wrapper short-circuit to a real 404 before caching, so
// stale links don't cache as 200 soft-404s at unique URLs.
export const isMissingJobInLoaderData = (idToLoaderData: IIdToLoaderData): boolean => {
  for (const loaderData of Object.values(idToLoaderData)) {
    if (loaderData && 'seoOverride' in loaderData && loaderData.seoOverride === null) {
      return true;
    }
  }
  return false;
};

// when a no-code landing page needs to preload an image for performance reasons,
// it can include a `preloadImages` field in the loader data
// this function crunches the loader data and pulls out all the preload images
// so that the top-level page can include those preload urls in the Head section
export const getPreloadsFromLoaderData = (idToLoaderData: IIdToLoaderData) => {
  return Object.values(idToLoaderData)
    .map((loaderData: any) => {
      return loaderData?.preloadImages;
    })
    .filter((preload: any) => preload)
    .flat();
};

export const getLandingpageV2ServerSideProps: CacheSafeGetServerSideProps<
  LandingPageV2Props
> = async (context) => {
  if (!context.params?.slug) {
    return {
      notFound: true,
    };
  }

  let pageNum = 1;
  if (context.query.page) {
    pageNum = parseInt(context.query.page as string);
  }
  if (pageNum < 1) pageNum = 1;

  // collapse the slug from an array to a single string
  let slug = '';
  // if slug is a string and not an array
  if (typeof context.query.slug === 'string') {
    slug = context.query.slug;
  } else if (Array.isArray(context.query.slug)) {
    slug = context.query.slug.join('/');
  }

  try {
    const props = await fetchLandingPageV2BySlug({
      slug,
      requestUrl: context.resolvedUrl,
      pageNum,
      locale: 'en-US',
    });

    if (!props) {
      return {
        notFound: true,
      };
    }
    // check if the page is set to public, and reject if appropriate
    // this is intended to block non-public pages from being directly accessed by users,
    // while still letting them be used in experiments as treatments
    if (context.query.public && props.public === false) {
      return {
        notFound: true,
      };
    }
    let showModal = false;
    const excludedSlugs = [
      'builder',
      'agent',
      'real-estate',
      'homepage',
      'login',
      'register',
      'reset-password',
      'calculator',
      'careers',
      'safety',
      'privacy',
    ];

    let modalProps;
    if (excludedSlugs.every((excluded) => !slug.includes(excluded))) {
      showModal = true;
      modalProps = await getCashOfferModal();
    }
    return {
      props: {
        ...props,
        modalFields:
          showModal && modalProps
            ? {
                title: modalProps.fields.title,
                description: modalProps.fields.description,
                analyticsId: modalProps.fields.analyticsId,
              }
            : null,
        showModal: !!(showModal && modalProps),
      },
    };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
    return {
      notFound: true,
    };
  }
};

const LandingPageV2Layout = <T extends ILandingPageV2LayoutFields>(
  props: ILandingPageV2LayoutProps<T>,
) => {
  const contextualData = { landingPageSlug: props.fields.slug };

  const fields = props.fields as typeof props.fields & {
    body?: { content?: Array<{ data?: { target?: { fields?: { analyticsId?: string } } } }> };
  };
  const isTreatmentHeader = fields.body?.content?.some((node) =>
    isTreatmentAnalyticsId(node.data?.target?.fields?.analyticsId),
  );

  const imagePreloads = getContentPageImagePreloads(props.idToLoaderData);
  const jobPostingSchema = getJobPostingSchemaFromLoaderData(props.idToLoaderData);
  const seoOverride = getSeoOverrideFromLoaderData(props.idToLoaderData);

  const seoBaseFields: ISeoBaseFieldsFields | undefined = seoOverride
    ? {
        ...props.fields.seoBase?.fields,
        title: seoOverride.title,
        canonicalUrl: seoOverride.canonicalUrl,
        metaDescription:
          seoOverride.metaDescription ?? props.fields.seoBase?.fields?.metaDescription,
      }
    : props.fields.seoBase?.fields;

  const openGraphFields: IOpenGraphFieldsFields | undefined = seoOverride
    ? {
        ...props.fields.openGraph?.fields,
        title: seoOverride.ogTitle ?? seoOverride.title,
        url: seoOverride.ogUrl ?? seoOverride.canonicalUrl,
        description:
          seoOverride.ogDescription ??
          seoOverride.metaDescription ??
          props.fields.openGraph?.fields?.description ??
          '',
        type: props.fields.openGraph?.fields?.type ?? 'website',
        image:
          props.fields.openGraph?.fields?.image ??
          'https://www.opendoor.com/icons/opendoor-logo.svg',
        siteName: props.fields.openGraph?.fields?.siteName ?? 'Opendoor',
      }
    : props.fields.openGraph?.fields;

  const heroImage = !openGraphFields ? getHeroImageUrl((props.fields as any).body) : undefined;

  const appleStatusBarColor =
    props.fields.slug === 'homepage-syw-v2-home-guide' ? 'black' : 'default';

  const shouldIncludeTrustpilot = props.fields?.shouldShowTrustpilotReviews;

  // Check if this is a market page (slug format: landing-pages-v2/market-[market])
  const isMarketPage = props.fields.slug.startsWith('landing-pages-v2/market-');
  // Convert Contentful slug to public URL format for markdown alternate link
  const publicSlug = isMarketPage
    ? props.fields.slug.replace(/^landing-pages-v2\/market-/, '/sell/')
    : null;

  return (
    <AddContextualData data={contextualData}>
      <Head>
        {getSeoBaseTags(seoBaseFields)}
        {openGraphFields
          ? getOpenGraphTags(openGraphFields)
          : seoBaseFields && (
              <>
                {seoBaseFields.title && (
                  <meta key="og:title" property="og:title" content={seoBaseFields.title} />
                )}
                <meta key="og:type" property="og:type" content="website" />
                {seoBaseFields.canonicalUrl && (
                  <meta key="og:url" property="og:url" content={seoBaseFields.canonicalUrl} />
                )}
                {seoBaseFields.metaDescription && (
                  <meta
                    key="og:description"
                    property="og:description"
                    content={seoBaseFields.metaDescription}
                  />
                )}
                {heroImage && <meta key="og:image" property="og:image" content={heroImage} />}
                <meta key="og:site_name" property="og:site_name" content="Opendoor" />
                {seoBaseFields.title && (
                  <meta key="twitter:title" name="twitter:title" content={seoBaseFields.title} />
                )}
                {seoBaseFields.metaDescription && (
                  <meta
                    key="twitter:description"
                    name="twitter:description"
                    content={seoBaseFields.metaDescription}
                  />
                )}
                {seoBaseFields.canonicalUrl && (
                  <meta key="twitter:url" name="twitter:url" content={seoBaseFields.canonicalUrl} />
                )}
                {heroImage && <meta key="twitter:image" name="twitter:image" content={heroImage} />}
              </>
            )}
        {/* Third Audience: Markdown alternate link (RSS auto-discovery pattern) for market pages */}
        {isMarketPage && publicSlug && (
          <link
            id="markdown-alternate"
            key="markdown-alternate"
            rel="alternate"
            type="text/markdown"
            href={`${publicSlug}.md`}
          />
        )}
        <meta name="apple-mobile-web-app-status-bar-style" content={appleStatusBarColor} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(HOMEPAGE_SCHEMA) }}
        />
        {/* JSON-LD Schema for city pages (SEO structured data) */}
        {props.fields.jsonLdSchema && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(props.fields.jsonLdSchema).replace(/</g, '\\u003c'),
            }}
          />
        )}
        {jobPostingSchema && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(jobPostingSchema).replace(/</g, '\\u003c'),
            }}
          />
        )}
        {shouldIncludeTrustpilot && (
          <script
            type="text/javascript"
            src="//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js"
            async
          />
        )}
        {imagePreloads.map((h) => (
          <link
            key={h.key}
            rel="preload"
            as="image"
            href={h.fallbackSrc}
            media={h.media}
            imageSrcSet={h.srcSet}
            fetchPriority="high"
          />
        ))}
      </Head>
      {/* slash is to match against nav links for detecting current page */}
      <Layout
        header={props.fields?.header}
        slug={`/${props.fields?.slug}`}
        footer={props.fields?.footer}
        showModal={props.showModal}
        modalTitle={props.modalFields?.title}
        modalDescription={props.modalFields?.description}
        modalAnalyticsId={props.modalFields?.analyticsId}
        extraLegalText={props.fields?.extraLegalText}
        showEligibilityLegal={props.fields?.showEligibilityLegal}
        showCustomerTestimonialLegal={props.fields?.showCustomerTestimonialLegal}
        showThePdpCopyrightLegal={props.fields?.showThePdpCopyrightLegal}
        showPromotionLegal={props.fields?.showPromotionLegal}
        headerVariant={isTreatmentHeader ? 'treatment' : undefined}
      >
        {props.children}
      </Layout>
    </AddContextualData>
  );
};

export default LandingPageV2Layout;
